import type { SupabaseClient } from '@supabase/supabase-js';

import type { DestinationAccountOption } from '@/components/ui/inspector/bodies/post/DestinationAccountPicker';
import type { ConnectedAccount } from '@/hooks/usePlatformIntegrations';

/**
 * The accounts a post can publish as, for one platform. One option per
 * connected account, except LinkedIn: one connection carries a member
 * (personal profile) and/or organization (Company Page) resource, and each is
 * its own option. Shared by the post inspector's picker and the "choose where
 * this posts" prompt so both read the same list.
 */
export function buildDestinationOptions(
  accounts: ConnectedAccount[],
  platform: string | null | undefined,
): DestinationAccountOption[] {
  if (!platform) return [];
  return accounts
    .filter((account) => account.platform === platform)
    .flatMap((account) => {
      const linkedInResources =
        platform === 'linkedin'
          ? (account.selectedResources ?? []).filter(
              (resource) =>
                resource.resourceType === 'linkedin_member' ||
                resource.resourceType === 'linkedin_organization',
            )
          : [];
      if (linkedInResources.length === 0) {
        return [
          {
            accountId: account.accountId,
            displayName: account.displayName,
            identifier: account.identifier,
            imageUrl: account.imageUrl,
          },
        ];
      }
      return linkedInResources.map((resource) => ({
        accountId: account.accountId,
        resourceId: resource.id,
        displayName: resource.displayName,
        identifier: resource.accountType === 'organization' ? 'Company Page' : 'Personal profile',
        imageUrl: resource.imageUrl ?? account.imageUrl,
      }));
    });
}

export const LINKEDIN_DESTINATION_REQUIRED = 'LINKEDIN_DESTINATION_REQUIRED';
/** Distinct from schedule-post's own refusal, so callers can drop their error toast while the prompt is open and
 *  still show the server's words when it refuses on its own. */
export const LINKEDIN_DESTINATION_REQUIRED_MESSAGE =
  'Waiting for you to choose whether this LinkedIn post goes out as your profile or a Company Page.';

/** True for the app's own "choose where this posts" refusal: the prompt is already open, so no error toast. */
export function isAwaitingLinkedInDestination(error: unknown): boolean {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'object' && error && 'message' in error
        ? String((error as { message: unknown }).message)
        : '';
  return message === LINKEDIN_DESTINATION_REQUIRED_MESSAGE;
}
export const LINKEDIN_DESTINATION_EVENT = 'mora:linkedin-destination-required';

export interface LinkedInDestinationRequest {
  postId: string;
  /** What the person was doing, shown on the confirm button: "Schedule" / "Post now". */
  actionLabel: string;
  /** Re-runs the action once the choice is saved. Resolves to an error message, or null on success. */
  retry: () => Promise<string | null>;
}

/** Ask the app shell to open the "choose where this posts" prompt for a post. */
export function requestLinkedInDestinationChoice(request: LinkedInDestinationRequest): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(LINKEDIN_DESTINATION_EVENT, { detail: request }));
}

type Ids = Record<string, string | null> | null | undefined;

const asIds = (value: unknown): Ids =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, string | null>)
    : null;

/**
 * True when a LinkedIn post has no stored profile/Page choice and more than one
 * is selected for publishing, so publishing it would have to guess. Mirrors the
 * refusal in the `schedule-post` edge function, which stays the backstop; this
 * lets the app lead the person to the picker before the call.
 */
export async function linkedInDestinationChoiceMissing(
  client: SupabaseClient,
  row: {
    user_id: string;
    brand_kit_id?: string | null;
    destination_connection_ids?: unknown;
    destination_resource_ids?: unknown;
  },
): Promise<boolean> {
  // A failed lookup falls through to the edge function's own refusal, which
  // is the backstop; the app never blocks a post on a lookup it could not make.
  try {
    const connectionId = asIds(row.destination_connection_ids)?.linkedin ?? null;
    const resourceId = asIds(row.destination_resource_ids)?.linkedin ?? null;
    if (resourceId) return false;

    let connections = client
      .from('user_connections')
      .select('id')
      .eq('user_id', row.user_id)
      .eq('platform', 'linkedin')
      .eq('is_active', true);
    if (connectionId) connections = connections.eq('id', connectionId);
    // Only the post's own workspace: another workspace's Company Page is never this post's choice (tenancy §7.3).
    else if (row.brand_kit_id) connections = connections.eq('brand_kit_id', row.brand_kit_id);
    const { data: connectionRows, error: connectionError } = await connections;
    if (connectionError || !connectionRows || connectionRows.length === 0) return false;

    const { data: resources, error: resourceError } = await client
      .from('integration_selected_resources')
      .select('id')
      .eq('owner_id', row.user_id)
      .eq('platform', 'linkedin')
      .eq('is_selected', true)
      .in(
        'connection_id',
        connectionRows.map((connection) => connection.id),
      )
      .in('resource_type', ['linkedin_member', 'linkedin_organization']);
    if (resourceError) return false;
    return (resources?.length ?? 0) > 1;
  } catch {
    return false;
  }
}
